// Color section — the drawer's paint surface, now a FULL-DENSITY host of the
// house PaintPicker standard (owner, 2026-07-20: one color/texture/gradient
// code path everywhere). The picker's own Solid | Gradient | Texture header
// replaced this file's Fill-type segmented + swatch rows + stop chips +
// texture grid; what stays here is exactly what is HOST law, byte-compatible
// with the old writes:
//
// SELECTION-AWARE solids (ELA-tools lane, 2026-07-19): a solid pick routes
// through editor.forSelectionOrObject — highlighted characters get a
// per-char fill (works on 3D too: per-glyph materials), no selection means
// the whole-object setTextFill({type:'solid',color}) exactly as before.
// Gradients/textures are ALWAYS whole-object recipes (setTextFill(FillValue)
// — same objects the old chips wrote, GRADIENT_PRESETS references included)
// and the one-line hint below says so while characters are highlighted.
// Commit grammar: discrete picks 'now'; solid drag ticks 'soon'; recipe drag
// ticks (angle/stop/scale) 'defer' + ONE commitNow on release.
//
// "Browse all textures…" opens the house TextureExplorerModal (apply-mode,
// unchanged). "Reset character styling" keeps its quiet home at the bottom.
//
// NOTE (standard-alignment, 2026-07-20): the old segmented's
// click-Gradient-applies-preset-#1 shortcut is gone — the picker's tabs
// BROWSE (the ColorFlyoutPanel law); the first ramp click applies. One extra
// click, no surprise writes from a tab press.
import React, { useState } from 'react';
import PaintPicker from '../../primitives/PaintPicker';
import TextureExplorerModal from './TextureExplorerModal';

const hexOr = (v, fallback) => (typeof v === 'string' && v.startsWith('#') ? v : fallback);

export default function FillSection({ objProps, apply, commitNow, editor }) {
  const fv = objProps.textFill;

  // Texture Explorer visibility — pure UI; survives fill changes on purpose
  // (the modal applies textures live and stays open between tries).
  const [explorerOpen, setExplorerOpen] = useState(false);

  const currentSolid =
    typeof fv === 'string' ? hexOr(fv, '#111827')
      : fv?.type === 'solid' ? hexOr(fv.color, '#111827')
        : null;

  // Per-char override common to the current range (sparse — absent when none
  // or mixed): shown over the object recipe so the panel stays honest about
  // what a pick just did.
  const selFill = typeof objProps.selectionStyle?.fill === 'string'
    ? objProps.selectionStyle.fill
    : null;

  // ONE routing law, engine-side (unchanged): highlighted characters get a
  // per-char fill, else the whole-object recipe path runs.
  const pickSolid = (color, commitMode = 'now') => apply(
    () => editor?.forSelectionOrObject(
      { fill: color },
      () => editor?.setTextFill({ type: 'solid', color }),
    ),
    commitMode,
  );

  // The picker's value: a live per-char solid wins the display; else the
  // recipe; else the flat solid.
  const value = selFill ?? (fv && typeof fv === 'object' ? fv : (currentSolid ?? '#111827'));

  return (
    <>
      {objProps.textEffects?.stroke?.id === 'hollow' && (
        <div className="cce-hint">Coloring the outline</div>
      )}
      <PaintPicker
        value={value}
        density="full"
        allow={['solid', 'gradient', 'texture']}
        onChange={(next, { live } = {}) => {
          if (typeof next === 'string') pickSolid(next, live ? 'soon' : 'now');
          else apply(() => editor?.setTextFill(next), live ? 'defer' : 'now');
        }}
        onCommit={commitNow}
        onBrowseAll={() => setExplorerOpen(true)}
      />
      {objProps.hasCharSelection && (
        <div className="cce-hint">
          Solid colors restyle just the letters you selected — gradients and
          textures always color all of the text.
        </div>
      )}
      {explorerOpen && (
        <TextureExplorerModal
          objProps={objProps}
          apply={apply}
          commitNow={commitNow}
          editor={editor}
          onClose={() => setExplorerOpen(false)}
        />
      )}

      {/* The way OUT of per-char styles (replaces Emphasize's "Clear
          emphasis"): clears the highlighted range while editing, the whole
          string otherwise (clearSelectionTextStyle's own range rule). Only
          rendered while per-char overrides exist — hasCharStyles is falsy
          for non-text, so this never shows on other object types. */}
      {objProps.hasCharStyles && (
        <div className="cce-emph-row">
          <button
            type="button"
            className="cce-fx-textbtn cce-char-reset"
            onClick={() => apply(() => editor?.clearSelectionTextStyle(), 'now')}
          >
            Reset character styling
          </button>
        </div>
      )}
    </>
  );
}
